import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    // eslint-disable-next-line no-unused-vars
    _,
    // eslint-disable-next-line no-unused-vars
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return matchesFilterResult && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      // Mine tab shows only 'open' status conversations assigned to me
      const isOpenStatus = conversation.status === 'open';
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && isOpenStatus && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getCaptainChats:
    (
      _state,
      // eslint-disable-next-line no-unused-vars
      _,
      // eslint-disable-next-line no-unused-vars
      __,
      // eslint-disable-next-line no-unused-vars
      rootGetters
    ) =>
    activeFilters => {
      const filtered = _state.allConversations.filter(conversation => {
        // For Captain tab, we need to apply filters but override status filter to 'all'
        // so that ai_handling conversations pass through
        const filtersForCaptain = { ...activeFilters, status: 'all' };
        const shouldFilter = applyPageFilters(conversation, filtersForCaptain);

        // Captain tab always shows ai_handling conversations with no assignee
        // Check status - handle both string and number formats
        const status = String(conversation.status || '')
          .toLowerCase()
          .trim();
        const isAIHandled = status === 'ai_handling';

        // Check if assignee exists - check both meta.assignee and direct assignee_id
        // If assignee_id is explicitly set to a number, it has an assignee
        // If meta.assignee exists and has an id, it has an assignee
        // Otherwise, no assignee
        const assigneeId = conversation.assignee_id;
        const metaAssignee = conversation.meta?.assignee;
        const hasAssignee =
          (assigneeId !== null &&
            assigneeId !== undefined &&
            assigneeId !== 0) ||
          (metaAssignee && metaAssignee.id);
        const hasNoAssignee = !hasAssignee;

        const result = isAIHandled && hasNoAssignee && shouldFilter;

        return result;
      });

      return filtered;
    },
  getMissedChats:
    (
      _state,
      // eslint-disable-next-line no-unused-vars
      _,
      // eslint-disable-next-line no-unused-vars
      __,
      // eslint-disable-next-line no-unused-vars
      rootGetters
    ) =>
    activeFilters => {
      return _state.allConversations.filter(conversation => {
        // For Missed tab, we need to apply filters but override status filter to 'all'
        // so that all missed conversations pass through regardless of status
        const filtersForMissed = { ...activeFilters, status: 'all' };
        const shouldFilter = applyPageFilters(conversation, filtersForMissed);

        // Missed tab shows conversations that haven't been responded to (waiting_since is not null)
        const hasWaitingSince = conversation.waiting_since !== null && conversation.waiting_since !== undefined;

        return hasWaitingSince && shouldFilter;
      });
    },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const allowedForRole = applyRoleFilter(
        conversation,
        userRole,
        permissions,
        currentUserId
      );

      return shouldFilter && allowedForRole;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
